/**
 * EL ESTADO DE AGENDA DE UN CHAT — un solo lugar, tres consumidores.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR QUÉ ESTE SE CALCULA Y EL DE VENTA SE PERSISTE (23-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * `seguimientoVenta.js` lleva escrito su propio argumento —«POR QUÉ CALCULADO Y NO
 * PERSISTIDO»— y lo aplica sólo al estado de 48 h: el hecho de la venta sí lo
 * denormaliza al chat. Y hace bien, porque **ese hecho vive en Shopify**, fuera de
 * Firestore: no hay de dónde calcularlo sin salir a la red.
 *
 * Una reserva es nuestra y ya está en Firestore, en `{tenant}/bookings`, con `chatId`
 * dentro (medido el 24-sep-2026: 94 de 94 lo llevan, igual que `status`, `start` y
 * `createdAt`). Así que el mismo razonamiento cubre el marcador ENTERO.
 *
 * Y denormalizarlo costaría TRES escritores, no el que confirma (eran cinco hasta el 14-oct-2026,
 * cuando se retiró el circuito de pendingBookings con `reconcilePendingBookings.js` y
 * `callable/retryPendingBooking.js`, que también confirmaban):
 *
 *   1. `bot/utils/toolRouter.js`  createBooking, la tool del bot
 *   2. `bot/utils/toolRouter.js`  cancelBooking — escribe `status:'cancelled'`
 *                                 con su `cancelledAt`, NO borra
 *   3. `bot/utils/toolRouter.js`  modifyBooking — mueve la fecha
 *
 * Los dos últimos son los que condenan la copia: un chat marcado «agendó» cuya única
 * cita se canceló es el sistema afirmando algo que ya no es cierto — la familia que
 * `afirmacionDeAccion` persigue. Calculado, cancelar desmarca gratis.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL EJE ES FUTURO/PASADO, NO FRESCURA
 * ───────────────────────────────────────────────────────────────────────────────
 * En la venta el eje es el tiempo transcurrido (48 h y pasa a «vencida»), porque lo
 * accionable es el seguimiento reciente. En una cita lo accionable es **que todavía no
 * ha ocurrido**: una reunión mañana a las 15:00 pide algo hoy; la de la semana pasada
 * es historia. Copiar la ventana de 48 h habría marcado como «activa» una cita ya
 * celebrada y como «vencida» una que aún no llega.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ CUÁL SE ENSEÑA CUANDO NO HAY NINGUNA POR DELANTE (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * EL CASO: Rodrigo canceló por conversación la cita de hoy a las 10:00 y la huincha
 * siguió diciendo «🗓️ Cita pasada · 2026-09-15» — una reunión de hace nueve días. El
 * estado era exacto sobre los documentos y **inútil sobre la conversación**: lo último
 * que había pasado con la agenda era la cancelación, y no se veía por ningún lado.
 *
 * La regla, en tres pasos:
 *
 *   1 · ¿hay confirmada por delante?  → `proxima`, la más cercana
 *   2 · si no → la del HECHO MÁS RECIENTE:  confirmed → `pasada`
 *                                           cancelled → `cancelada`
 *   3 · si ninguna tiene instante legible → `pasada` si hay alguna confirmada
 *
 * **La próxima gana siempre, y ése es el paso que no se puede tocar.** Cancelar una
 * cita vieja no puede tapar una viva: lo accionable es lo que aún no ha ocurrido.
 *
 * ⚠️ NO HAY UMBRAL, y se descartó habiéndolo considerado. «Si hay una cancelación de
 * las últimas 24 h, enséñala» funciona y obliga a defender el 24: cancelar a las 07:05
 * y abrir el chat 25 horas después devolvería la cita de hace nueve días. Además mete
 * un reloj en la decisión y la vuelve no determinista. «La más reciente» no tiene
 * número que defender.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ `instanteDelHecho` ES ASIMÉTRICO A PROPÓSITO — Y NO ES «LA FECHA DE CAMBIO»
 * ───────────────────────────────────────────────────────────────────────────────
 * La variante fácil —comparar cuándo se tocó cada documento, `createdAt` contra
 * `cancelledAt`— está MAL, y el caso que la mata es concreto:
 *
 *     Reserva A: creada el 1-ago, REUNIÓN EL 20-sep   (ocurrió)
 *     Reserva B: creada el 1-jul, cancelada el 10-sep
 *
 * Por fecha de cambio gana B (10-sep > 1-ago) y la huincha diría «cancelada» a alguien
 * que **tuvo una reunión hace cuatro días**. Es el mismo defecto que esto viene a
 * arreglar, girado.
 *
 * Lo que hay que comparar no es cuándo se tocó el documento sino **cuándo pasó lo que
 * la reserva dice**:
 *
 *   · `confirmed` → su hecho es la reunión        → `start`       (respaldo `createdAt`)
 *   · `cancelled` → su hecho es la cancelación    → `cancelledAt` (respaldo `createdAt`)
 *
 * Con eso A (20-sep) gana a B (10-sep) y sale «Cita pasada»; y en el caso de Rodrigo la
 * cancelación de hoy gana a la reunión del 15-sep. Las dos correctas. El nombre dice
 * «hecho» y no «cambio» para que la asimetría esté a la vista y nadie la «uniforme».
 *
 * MEDIDO ANTES DE ESCRIBIRLO, las dos reglas sobre las 94 reservas reales: de 20 chats
 * con reserva **cambian 2** —los dos de `pasada` a `cancelada`, uno es el caso—. Los
 * otros 18 dan idéntico. En particular, **en un chat sin cancelaciones la regla nueva
 * elige exactamente la misma reserva que la vieja**: entre confirmadas, «el hecho más
 * reciente» ES «el `start` más reciente».
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * SÓLO `confirmed` Y `cancelled` LLEGAN AQUÍ, Y POR ESO SON LAS ÚNICAS ELEGIBLES
 * ───────────────────────────────────────────────────────────────────────────────
 * A `{tenant}/bookings` —lo que estas funciones reciben— sólo llegan `confirmed` y `cancelled`.
 * Los otros cuatro `status` —`pending`, `expired`, `failed`, `manual_required`/`manual_handled`—
 * vivían en `{tenant}/pendingBookings`, un circuito que se retiró el 14-oct-2026 sin haber
 * tenido nunca un documento (su escritor no tenía llamantes).
 *
 * Aun así el filtro es EXPLÍCITO en vez de dar por hecho que no aparecerán, porque la
 * alternativa era heredar el viejo `return 'cancelada'` de cajón de sastre: una cita
 * `pending` —el bot la prometió y el calendario no respondió— se habría pintado
 * «🚫 Cita cancelada». Falso, y falso de la peor manera: *tranquilizador*, porque le
 * dice al operador «esto está zanjado» justo en el chat que necesita que alguien mire.
 *
 * (Aquí había un HUECO CONOCIDO: una reserva `pending` sólo se veía en
 * `PendingBookingsPanel.jsx`. Panel y colección se retiraron el 14-oct-2026; 0 documentos
 * medidos el 24-sep y el 14-oct.)
 */

/** Milisegundos de un campo de fecha, en las cuatro formas en que puede llegar. */
function instanteDe(valor) {
  if (!valor) return NaN;
  if (typeof valor.toDate === 'function') return valor.toDate().getTime();   // Timestamp
  if (valor instanceof Date) return valor.getTime();
  if (typeof valor === 'number') return valor;
  const parsed = Date.parse(valor);
  return Number.isFinite(parsed) ? parsed : NaN;
}

/**
 * El instante de una reserva — CUÁNDO ES LA CITA.
 *
 * `start` está en 94 de 94 reservas y es el instante UTC correcto, así que es el único
 * campo que se mira. NO se compone de `date` + `time`: esos dos son hora local del
 * negocio y sin `bookingTimeZone` —ausente en 24 de 90 cuando se midió— componerlos
 * daría un instante desplazado sin avisar. Una reserva sin `start` se trata como
 * inexistente, que es honesto: no sabemos cuándo es.
 */
export function instanteDeReserva(reserva) {
  return instanteDe(reserva?.start);
}

/** ¿Cuenta como reserva viva? Sólo `confirmed`. */
export function estaConfirmada(reserva) {
  return reserva?.status === 'confirmed';
}

/** ¿Está cancelada? Ver la cabecera: es el otro único estado que llega a `bookings`. */
export function estaCancelada(reserva) {
  return reserva?.status === 'cancelled';
}

/**
 * ¿Puede esta reserva salir en la huincha?
 *
 * Ver la cabecera. Un `status` desconocido NO se pinta: prefiero que no diga nada a que
 * diga algo falso y tranquilizador.
 */
export function cuentaParaLaHuincha(reserva) {
  return estaConfirmada(reserva) || estaCancelada(reserva);
}

/**
 * CUÁNDO PASÓ LO QUE ESTA RESERVA DICE.
 *
 * ⚠️ Asimétrico a propósito — ver la cabecera, con el caso que mata la variante fácil.
 * Devuelve `NaN` si no hay ningún campo legible, y quien compara **tiene que saltarse
 * el NaN explícitamente**: un `Math.max` lo propagaría y envenenaría la comparación
 * entera, que es cómo una reserva sin fecha desordenaría a todas las demás.
 */
export function instanteDelHecho(reserva) {
  if (!reserva) return NaN;
  const cadena = estaCancelada(reserva)
    ? [reserva.cancelledAt, reserva.createdAt]     // su hecho es la cancelación
    : [reserva.start, reserva.createdAt];          // su hecho es la reunión
  for (const valor of cadena) {
    const t = instanteDe(valor);
    if (Number.isFinite(t)) return t;
  }
  return NaN;
}

/**
 * La próxima cita confirmada, o null.
 *
 * Devuelve la MÁS CERCANA en el futuro, no la primera del array: el orden en que
 * Firestore devuelve los documentos no es cronológico y confiar en él haría que la
 * píldora mostrara una fecha correcta unas veces y otra cita el resto.
 */
export function proximaReserva(reservas, ahora = Date.now()) {
  if (!Array.isArray(reservas)) return null;
  let mejor = null;
  let mejorT = Infinity;
  for (const r of reservas) {
    if (!estaConfirmada(r)) continue;
    const t = instanteDeReserva(r);
    if (!Number.isFinite(t) || t < ahora) continue;
    if (t < mejorT) { mejor = r; mejorT = t; }
  }
  return mejor;
}

/**
 * QUÉ CITA ENSEÑA LA HUINCHA, Y CON QUÉ RÓTULO. El único sitio donde se decide.
 *
 * Antes cada pantalla componía `proximaReserva() || ultimaReservaPasada()` por su
 * cuenta —cuatro llamadas y una composición a mano, duplicadas en dos ficheros—, que es
 * justo donde dos consumidores divergen sin que nada se ponga rojo.
 *
 * @param {Array} reservas - las reservas de ESE chat, ya unidas por `chatId`
 * @param {number} [ahora]
 * @returns {{estado:'ninguna'|'proxima'|'pasada'|'cancelada', cita:object|null}}
 */
export function citaDestacada(reservas, ahora = Date.now()) {
  if (!Array.isArray(reservas)) return { estado: 'ninguna', cita: null };
  const elegibles = reservas.filter(cuentaParaLaHuincha);
  if (elegibles.length === 0) return { estado: 'ninguna', cita: null };

  // 1 · LO ACCIONABLE GANA SIEMPRE. Una cancelación reciente no puede tapar una cita
  //     viva: el operador necesita saber que esta persona viene el jueves.
  const proxima = proximaReserva(elegibles, ahora);
  if (proxima) return { estado: 'proxima', cita: proxima };

  // 2 · Nada por delante: lo último que pasó con la agenda de este chat.
  let mejor = null;
  let mejorT = -Infinity;
  for (const r of elegibles) {
    const t = instanteDelHecho(r);
    // ⚠️ REDUNDANTE HOY, Y SE QUEDA. `NaN > mejorT` ya es `false`, así que una reserva
    //    sin fecha no gana ni sin esta línea — comprobado por mutación: quitarla no pone
    //    nada en rojo. Lo que SÍ rompe es reescribir este bucle como
    //    `sort((a,b) => instanteDelHecho(b) - instanteDelHecho(a))[0]`, porque con NaN el
    //    orden queda indefinido y la reserva sin fecha puede salir primera. Esa mutación
    //    pone 3 tests en rojo. La guarda está por ese refactor, no por el bucle de hoy.
    if (!Number.isFinite(t)) continue;
    if (t > mejorT) { mejor = r; mejorT = t; }
  }
  if (mejor) return { estado: estaConfirmada(mejor) ? 'pasada' : 'cancelada', cita: mejor };

  // 3 · Hay reservas y ninguna con instante legible. Se rotula sin fecha: decir «canceló»
  //     de una cita que existe es peor que decir «tuvo una».
  return { estado: elegibles.some(estaConfirmada) ? 'pasada' : 'cancelada', cita: null };
}

/**
 * El estado de agenda de un chat.
 *
 * Conserva la firma porque la usa el filtro «Solo con agenda» (`Chats.js`), al que no
 * le hace falta saber QUÉ cita se enseña.
 *
 * @returns {'ninguna'|'proxima'|'pasada'|'cancelada'}
 */
export function estadoSeguimientoAgenda(reservas, ahora = Date.now()) {
  return citaDestacada(reservas, ahora).estado;
}

/** ¿Hay que mostrar la marca? Las tres que no son `ninguna` se muestran. */
export function tieneAgendaVisible(reservas, ahora = Date.now()) {
  return estadoSeguimientoAgenda(reservas, ahora) !== 'ninguna';
}

/** Cuántas citas confirmadas ha tenido este chat, pasadas y futuras. */
export function totalDeReservas(reservas) {
  if (!Array.isArray(reservas)) return 0;
  return reservas.filter(estaConfirmada).length;
}

/**
 * Une las reservas de una organización a sus chats.
 *
 * DEVUELVE TAMBIÉN LAS HUÉRFANAS, y eso no es adorno. Medido el 24-sep-2026: 1 de los
 * 12 `chatId` con reserva de la organización principal apunta a un chat **que no
 * existe** —hoy `56981363182`—. Descartarlas en silencio dentro del bucle habría hecho
 * que ese número no existiera para nadie.
 *
 * ⚠️ EL IDENTIFICADOR CAMBIÓ, Y ESO ES EL DATO. Esta cabecera decía
 * `CL-1308223934704771`, medido el 23-sep. Hoy ese chat **existe** en la organización
 * principal —es el de Rodrigo, con 22 reservas— porque volvió a escribir. El recuento
 * (1 de 12) sigue valiendo; el id no. Es lo que CLAUDE.md llama «las notas describen un
 * estado, no una propiedad», y se destapó porque un control contradijo esta cabecera.
 * (El mismo id sigue huérfano en `HVW73kZY`, que es la deuda de las reservas sin
 * conversación. Ahí hay 3 de 5.)
 *
 * @param {Array} reservas - todas las de la organización
 * @param {Set|Map|Array} chatsConocidos - los ids de chat cargados
 * @returns {{porChat: Map<string, Array>, huerfanas: Array<{chatId:string, id:string}>}}
 */
export function indexarReservasPorChat(reservas, chatsConocidos) {
  const porChat = new Map();
  const huerfanas = [];
  if (!Array.isArray(reservas)) return { porChat, huerfanas };

  const conoce = (id) => {
    if (!chatsConocidos) return true;      // sin censo no se puede decidir: no se acusa
    if (chatsConocidos instanceof Set || chatsConocidos instanceof Map) return chatsConocidos.has(id);
    if (Array.isArray(chatsConocidos)) return chatsConocidos.includes(id);
    return true;
  };

  for (const r of reservas) {
    const chatId = r?.chatId;
    if (!chatId) continue;
    if (!conoce(chatId)) {
      huerfanas.push({ chatId, id: r.id || null, status: r.status || null });
      continue;
    }
    if (!porChat.has(chatId)) porChat.set(chatId, []);
    porChat.get(chatId).push(r);
  }
  return { porChat, huerfanas };
}
